/**
 * Pure session-quality ledger — upgrade violation oracle for sticky sessions.
 *
 * Records the applied encode/capture quality per peer per session epoch. Any increase in
 * bitrate, framerate, or capture-tier quality within an epoch is a violation (PRODUCT
 * DECISION: no mid-session upgrades after a sticky floor). Browser-free and Jest-safe.
 *
 * Tier quality order (higher index = better / higher quality). An "upgrade" moves toward
 * a higher index — that is what yields a violation:
 *   none < bottom < mid < top
 */

export type SessionQualitySample = {
  maxBitrateBps: number;
  maxFramerate: number;
  /** Capture / encode tier id (e.g. 'bottom' | 'mid' | 'top' | 'none'). */
  tier: string;
};

export type SessionQualityViolation = {
  peerId: string;
  epoch: number;
  field: 'maxBitrateBps' | 'maxFramerate' | 'tier';
  previous: number | string;
  next: number | string;
};

/** Documented tier severity for upgrade detection. Unknown tiers rank as 0 (same as none). */
const TIER_QUALITY_RANK: Record<string, number> = {
  none: 0,
  bottom: 1,
  mid: 2,
  top: 3,
};

function tierRank(tier: string): number {
  return TIER_QUALITY_RANK[tier] ?? 0;
}

type PeerEpoch = {
  epoch: number;
  last?: SessionQualitySample;
};

export class SessionQualityLedger {
  private readonly peers = new Map<string, PeerEpoch>();
  private readonly _violations: SessionQualityViolation[] = [];

  /** Start or advance a session epoch for `peerId`, clearing its applied sample. */
  beginEpoch(peerId: string): void {
    const existing = this.peers.get(peerId);
    this.peers.set(peerId, { epoch: (existing?.epoch ?? 0) + 1 });
  }

  /** Drop peer state (session ended). Does not clear historical violations. */
  endEpoch(peerId: string): void {
    this.peers.delete(peerId);
  }

  /**
   * Record an applied quality sample. Auto-begins epoch 1 if the peer has no epoch yet.
   * Any increase vs the previous sample in this epoch appends a violation.
   */
  record(peerId: string, sample: SessionQualitySample): void {
    let peer = this.peers.get(peerId);
    if (!peer) {
      this.beginEpoch(peerId);
      peer = this.peers.get(peerId)!;
    }

    const prev = peer.last;
    if (prev) {
      if (sample.maxBitrateBps > prev.maxBitrateBps) {
        this._violations.push({
          peerId,
          epoch: peer.epoch,
          field: 'maxBitrateBps',
          previous: prev.maxBitrateBps,
          next: sample.maxBitrateBps,
        });
      }
      if (sample.maxFramerate > prev.maxFramerate) {
        this._violations.push({
          peerId,
          epoch: peer.epoch,
          field: 'maxFramerate',
          previous: prev.maxFramerate,
          next: sample.maxFramerate,
        });
      }
      if (tierRank(sample.tier) > tierRank(prev.tier)) {
        this._violations.push({
          peerId,
          epoch: peer.epoch,
          field: 'tier',
          previous: prev.tier,
          next: sample.tier,
        });
      }
    }

    peer.last = {
      maxBitrateBps: sample.maxBitrateBps,
      maxFramerate: sample.maxFramerate,
      tier: sample.tier,
    };
  }

  violations(): readonly SessionQualityViolation[] {
    return this._violations;
  }

  snapshot(): { qualityUpgradesThisSession: number } {
    return { qualityUpgradesThisSession: this._violations.length };
  }

  reset(): void {
    this.peers.clear();
    this._violations.length = 0;
  }
}
